import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

type Message = { role: "user" | "assistant"; content: string };

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: "Hi! How are you doing today? 👋" },
  ]);
  const ASSISTANT_AVATAR = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=facearea&facepad=3&crop=faces";
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [pulse, setPulse] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const pulseTimer = setTimeout(() => setPulse(true), 2000);
    const followUpTimer = setTimeout(() => {
      setMessages((prev) =>
        prev.length === 1
          ? [...prev, { role: "assistant", content: "What's your website? I'll show you exactly what SEO you're missing. 🚀" }]
          : prev
      );
    }, 2500);
    return () => { clearTimeout(pulseTimer); clearTimeout(followUpTimer); };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);


  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;
    const win = window.visualViewport;
    if (!win) return;
    const handler = () => {
      const chatWindow = document.querySelector('.cw-window') as HTMLElement;
      if (!chatWindow) return;
      chatWindow.style.bottom = 'auto';
      chatWindow.style.top = `${win.offsetTop + 60}px`;
    };
    win.addEventListener('resize', handler);
    win.addEventListener('scroll', handler);
    return () => { win.removeEventListener('resize', handler); win.removeEventListener('scroll', handler); };
  }, [open]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMessage: Message = { role: "user", content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("chat-widget", {
        body: { messages: newMessages },
      });
      if (error) throw error;
      const reply = data?.reply || "Something went wrong. Try again!";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Connection issue — try again in a moment!" }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      <style>{`
        @keyframes cwPulse { 0%,100%{box-shadow:0 0 0 0 rgba(74,222,128,0.4),0 4px 20px rgba(0,0,0,0.4)} 50%{box-shadow:0 0 0 10px rgba(74,222,128,0),0 4px 20px rgba(0,0,0,0.4)} }
        @keyframes cwSlideUp { from{opacity:0;transform:translateY(20px) scale(0.95)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes cwFadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes cwTypingDot { 0%,100%{opacity:0.3;transform:translateY(0)} 50%{opacity:1;transform:translateY(-3px)} }
        .cw-msg{animation:cwFadeIn 0.3s ease forwards}
        .cw-td1{animation:cwTypingDot 1s ease infinite 0s}
        .cw-td2{animation:cwTypingDot 1s ease infinite 0.15s}
        .cw-td3{animation:cwTypingDot 1s ease infinite 0.3s}
        .cw-input:focus{border-color:rgba(74,222,128,0.4)!important;outline:none}
        @media(max-width:480px){
          .cw-bubble{width:48px!important;height:48px!important;bottom:16px!important;right:16px!important}
          .cw-window{position:fixed!important;top:60px!important;bottom:auto!important;left:16px!important;right:16px!important;width:auto!important;height:420px!important;max-height:420px!important}
          .cw-window .cw-messages{max-height:240px!important}
          .cw-tooltip{right:16px!important;bottom:72px!important}
        }
      `}</style>

      {/* Bubble */}
      <div
        className="cw-bubble"
        onClick={() => { setOpen(!open); setHasOpened(true); setPulse(false); }}
        style={{
          position: "fixed", bottom: 24, right: 24, width: 56, height: 56, borderRadius: "50%",
          background: "linear-gradient(135deg,#4ade80,#22c55e)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
          animation: pulse && !open ? "cwPulse 2s ease-in-out infinite" : "none",
          boxShadow: "0 4px 20px rgba(0,0,0,0.4)", transition: "transform 0.2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" /></svg>
        )}
      </div>

      {/* Tooltip */}
      {!open && pulse && (
        <div className="cw-tooltip" style={{
          position: "fixed", bottom: 88, right: 24, background: "rgba(17,24,39,0.95)",
          border: "1px solid rgba(74,222,128,0.2)", borderRadius: 12, padding: "10px 16px",
          zIndex: 9998, animation: "cwFadeIn 0.5s ease", cursor: "pointer",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }} onClick={() => { setOpen(true); setHasOpened(true); setPulse(false); }}>
          <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>💬 How's your SEO doing?</span>
        </div>
      )}

      {/* Chat window */}
      {open && (
        <div className="cw-window" style={{
          position: "fixed", bottom: 92, right: 24, width: 360, height: '500px', maxHeight: '500px',
          background: "linear-gradient(180deg,#0f1419,#111827)", border: "1px solid rgba(74,222,128,0.15)",
          borderRadius: 16, zIndex: 9998, display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)", animation: "cwSlideUp 0.3s ease",
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%", overflow: "hidden",
              border: "2px solid rgba(74,222,128,0.4)", flexShrink: 0,
            }}>
              <img src={ASSISTANT_AVATAR} alt="Assistant" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div>
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>Kelly from OrbitRank</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80" }} />
                Online — here to help
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="cw-messages" style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12, maxHeight: 340 }}>
            {messages.map((msg, i) => (
              <div key={i} className="cw-msg" style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 6 }}>
                {msg.role === "assistant" && (
                  <div style={{ width: 24, height: 24, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: "1px solid rgba(74,222,128,0.3)" }}>
                    <img src={ASSISTANT_AVATAR} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                )}
                <div style={{
                  maxWidth: "85%", padding: "10px 14px", borderRadius: 14, fontSize: 13, lineHeight: 1.5,
                  ...(msg.role === "user"
                    ? { background: "linear-gradient(135deg,#4ade80,#22c55e)", color: "#052e16", borderBottomRightRadius: 4, fontWeight: 500 }
                    : { background: "rgba(255,255,255,0.06)", color: "#e5e7eb", borderBottomLeftRadius: 4 }),
                }}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="cw-msg" style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 14, padding: "12px 18px", display: "flex", gap: 4 }}>
                  {["cw-td1", "cw-td2", "cw-td3"].map((c) => (
                    <div key={c} className={c} style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80" }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: 8 }}>
            <input
              className="cw-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              onFocus={() => window.scrollTo(0, 0)}
              placeholder="Type your website URL..."
              style={{
                flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 12, padding: "10px 14px", color: "#fff", fontSize: 16, fontFamily: "inherit",
              }}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              style={{
                width: 38, height: 38, borderRadius: 10, border: "none",
                background: input.trim() ? "linear-gradient(135deg,#4ade80,#22c55e)" : "rgba(255,255,255,0.05)",
                cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={input.trim() ? "#052e16" : "rgba(255,255,255,0.3)"}>
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
